// ============================================
// Progress Bar Components
// Design System v2.0 - Gradient fills
// ============================================

import { m } from 'framer-motion';
import { progressFill } from '@/animations';
import type { SRSStats } from '@/types';

// ============================================
// Simple Progress Bar
// ============================================

interface ProgressBarProps {
  value: number; // 0-100
  color?: 'primary' | 'srs-new' | 'srs-learning' | 'srs-review' | 'srs-mastered';
  height?: number;
  showLabel?: boolean;
}

export function ProgressBar({
  value,
  color = 'primary',
  height = 8,
  showLabel = false
}: ProgressBarProps) {
  const clampedValue = Math.max(0, Math.min(100, value));

  const colorClasses: Record<string, string> = {
    'primary': 'progress-fill-primary',
    'srs-new': 'progress-fill-srs-new',
    'srs-learning': 'progress-fill-srs-learning',
    'srs-review': 'progress-fill-srs-review',
    'srs-mastered': 'progress-fill-srs-mastered',
  };

  return (
    <div className="w-full">
      <div
        className="progress-bar"
        style={{ height: `${height}px` }}
        role="progressbar"
        aria-valuenow={clampedValue}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <m.div
          className={`progress-fill ${colorClasses[color]}`}
          variants={progressFill}
          initial="initial"
          animate="animate"
          custom={clampedValue}
        />
      </div>
      {showLabel && (
        <span className="text-sm text-slate-500 mt-1">
          {Math.round(clampedValue)}%
        </span>
      )}
    </div>
  );
}

// ============================================
// SRS Stats Bar (segmented)
// ============================================

interface SRSStatsBarProps {
  stats: SRSStats;
  total: number;
  /**
   * Render the count legend below the bar. Off where the same counts are
   * already shown as labeled cards next to the bar (avoids triplicating them);
   * on for any standalone use of the bar.
   */
  showLegend?: boolean;
}

export function SRSStatsBar({ stats, total, showLegend = true }: SRSStatsBarProps) {
  if (total === 0) return null;

  const segments = [
    { key: 'mastered', value: stats.mastered, gradient: 'progress-fill-srs-mastered', label: 'Mastered', dotColor: 'bg-srs-mastered' },
    { key: 'review', value: stats.review, gradient: 'progress-fill-srs-review', label: 'Review', dotColor: 'bg-srs-review' },
    { key: 'learning', value: stats.learning, gradient: 'progress-fill-srs-learning', label: 'Learning', dotColor: 'bg-srs-learning' },
    { key: 'new', value: stats.new, gradient: 'progress-fill-srs-new', label: 'New', dotColor: 'bg-srs-new' }
  ];

  return (
    <div className="w-full">
      <div className="flex w-full h-3 rounded-full overflow-hidden bg-slate-200">
        {segments.map(segment => {
          const percentage = (segment.value / total) * 100;
          if (percentage === 0) return null;

          return (
            <m.div
              key={segment.key}
              className={`h-full ${segment.gradient}`}
              initial={{ width: 0 }}
              animate={{ width: `${percentage}%` }}
              transition={{ duration: 0.6, ease: [0.33, 1, 0.68, 1] }}
              title={`${segment.label}: ${segment.value}`}
            />
          );
        })}
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3 text-xs">
          {segments.map(segment => (
            <div key={segment.key} className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded-full ${segment.dotColor}`} />
              <span className="text-slate-600 font-medium">
                {segment.label}: <span className="text-slate-800">{segment.value}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
